/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useContext, useEffect, useState } from "react";
import { TextField, Switch } from "@mui/material";
import LabelledField from "components/labelled-field";
import LinkCreateContext from "cms/links/link-selector/context/create";
import { WebpageSearch } from "../webpage-search";
import { objectHasPropertyString, isObject, isStringNotEmpty } from "utils/empty-exists";
import { useSelectedText } from "cms/rich-text/simplicity-composer-tiptap/hooks/use-selected-text";
import { useEditLink } from "cms/links/link-selector/dialog-edit/hooks/useEditLinks";
import { Logger } from "cms/utils/log/logger";

const defaultEmptyLink = {
    text: "",
    url: "",
    path: "",
    webpage: "",
    model: "",
    is_external: false,
    type: "external",
};

const LinkCreateMainPanel = () => {
    
    const {
        setLink,
        selectedLink,
        setCanSave,
        startClone,
    } = useContext(LinkCreateContext);
    
    const { selectedText, findSelection } = useSelectedText();
    const { classifyLink, validateLink } = useEditLink();
    
    // populate text if selected from richtext
    const emptyLink = structuredClone(defaultEmptyLink);
    const [linkState, setLinkState] = useState(emptyLink);
    const [integration, setIntegration] = useState(false);
    const showIntegration = linkState?.url && (!linkState?.url?.startsWith("mailto:")) && (linkState.link_type !== "internal");
    
    try {
        
        useEffect(() => {
            const newlinkState = structuredClone(linkState);
            newlinkState.text = selectedText;
            setLinkState(newlinkState);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectedText]);
        
        findSelection();
        
    } catch (error) {
        Logger.warn(error);
    }
    
    useEffect(() => {
        if (startClone) {
            const defaultNewName = objectHasPropertyString(selectedLink, "text")
                ? `${selectedLink?.text} (Copy)`
                : "";
            const duplicate = {
                ...selectedLink,
                text: defaultNewName
            };
            delete duplicate.guid;
            delete duplicate.id;
            setLinkState(duplicate);
            setLink(duplicate);
            setCanSave(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startClone]);

    const onChange = (value, property, integration) => {
        const change = {
            ...linkState,
            [property]: value
        };
        if (objectHasPropertyString(change, "webpage") && objectHasPropertyString(change, "path")) {
            change.url = change.path;
        }
        const _link = classifyLink(change,integration);
        setLinkState(_link);
        setLink(_link);
        const valid = validateLink(_link);
        setCanSave(valid);
        if (property === "url" && _link.link_type === "integration") {
            setIntegration(true);
        }
    };
    const onChooseWebpage = (webpage) => {
        if (isObject(webpage) && objectHasPropertyString(webpage, "guid")) {
            const link_type = "internal";
            const hasSelectedText = isStringNotEmpty(selectedText);
            const newText = hasSelectedText ? selectedText : webpage?.nav_title;
            const _link = {
                text: newText,
                url: webpage.path,
                webpage: webpage?.guid,
                model: webpage?.model,
                is_external: false,
                link_type: link_type,
            };
            setLink(_link);
            setLinkState(_link);
            // setCanSave(true)
            const valid = validateLink(_link);
            setCanSave(valid);
        } else {
            setLinkState(emptyLink);
            setLink(emptyLink);
            const valid = validateLink(emptyLink);
            setCanSave(valid);
        }
    };
    
    const onChangeText = (event) => {
        const value = event.target.value;
        onChange(value, "text");
    };
    const onChangeUrl = (event) => {
        const value = event.target.value;
        onChange(value, "url");
    };
    const onChangeIntegration = (event) => {
        const checked = event.target.checked;
        setIntegration(checked);
        onChange(checked, "type",checked);
    };

    const isInternal = objectHasPropertyString(linkState, "webpage");

    return (
        <Fragment>
                
            <div style={{ padding: "0 24px 24px 24px" }}>
                <div style={{
                    background: "#F3F8F9", borderRadius: "8px",
                    padding: "16px", fontSize: "11px"
                }}
                >
                    {!startClone &&
                    <LabelledField
                        label='Select a webpage to create an internal link'
                        variant='standard'
                        className='labelField'
                    >
                        <WebpageSearch
                            onChooseWebpage={onChooseWebpage}
                        />
                        <div><p>or, add an external URL below</p></div>
                    </LabelledField>
                    }
                    {startClone &&
                        <>
                            <div style={{ fontSize: "13px", fontWeight: "400", color: "#666666" }}>
                                Selected Webpage
                            </div>
                            <div style={{ fontSize: "16px", fontWeight: "500", color: "#2D2D2D" }}>
                                {selectedLink?.text}
                            </div>
                        </>
                    }
                </div>
                
                <LabelledField label='URL' className='labelField'>
                    <TextField
                        onChange={onChangeUrl}
                        id='link-url'
                        fullWidth
                        disabled={isInternal}
                        variant="outlined"
                        value={linkState?.url || ""}
                    />
                </LabelledField>
                
                <LabelledField label='Link text' className='labelField'>
                    <TextField
                        onChange={onChangeText}
                        id='link-text'
                        fullWidth
                        variant="outlined"
                        value={linkState?.text || ""}
                    />
                </LabelledField>
                {showIntegration &&
                    <LabelledField label="Hide External Icon (Integration)" className='labelField'>
                        <Switch
                            checked={integration}
                            onChange={onChangeIntegration}
                        />
                    </LabelledField>
                }
            </div>
            
        </Fragment>
    );
};

export default LinkCreateMainPanel;
